import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { MyProfile, UpdateMyProfileRequest } from '../models/my-profile.interface';

/** Formatos que acepta el backend para la foto. */
export const AVATAR_ALLOWED_TYPES = ['image/jpeg', 'image/png'];
/** Tamaño máximo de la foto: 2 MB. */
export const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Perfil del usuario logueado y su foto.
 *
 * La foto no se puede pintar con un `<img src>` apuntando a la API porque el
 * endpoint pide el `Authorization`, así que se descarga como blob (el
 * interceptor le pone el token) y se expone como object URL. Vive en un
 * servicio de raíz para que la cabecera y el modal compartan la misma foto y
 * se refresque en los dos sitios al subirla o borrarla.
 */
@Injectable({ providedIn: 'root' })
export class MyProfileService {
  private readonly apiUrl = environment.apiUrl;
  private http = inject(HttpClient);

  private readonly profileSignal = signal<MyProfile | null>(null);
  private readonly avatarUrlSignal = signal<string | null>(null);

  /**
   * Parámetro anti-caché del avatar. Arranca en la hora de carga de la página
   * (para que dos sesiones distintas no coincidan) y sube en cada petición.
   */
  private avatarCacheBuster = Date.now();

  /** Último perfil descargado, o `null` si todavía no se ha pedido. */
  readonly profile = this.profileSignal.asReadonly();
  /** Object URL de la foto, o `null` si el usuario no tiene. */
  readonly avatarUrl = this.avatarUrlSignal.asReadonly();

  getMyProfile(): Observable<MyProfile> {
    return this.http
      .get<MyProfile>(`${this.apiUrl}/users/me`)
      .pipe(tap((profile) => this.applyProfile(profile)));
  }

  updateMyProfile(body: UpdateMyProfileRequest): Observable<MyProfile> {
    return this.http
      .put<MyProfile>(`${this.apiUrl}/users/me`, body)
      .pipe(tap((profile) => this.applyProfile(profile)));
  }

  uploadAvatar(file: File): Observable<MyProfile> {
    const formData = new FormData();
    formData.append('file', file);

    // Sin `Content-Type` a mano: lo pone HttpClient con el boundary del multipart.
    return this.http.put<MyProfile>(`${this.apiUrl}/users/me/avatar`, formData).pipe(
      tap((profile) => {
        this.profileSignal.set(profile);
        // La respuesta ya confirma que la foto está guardada, pero el blob que
        // se pinta es el reescalado a 256px que devuelve el backend.
        this.loadAvatar();
      }),
    );
  }

  deleteAvatar(): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/users/me/avatar`).pipe(
      tap(() => {
        this.setAvatarUrl(null);
        const current = this.profileSignal();
        if (current) {
          this.profileSignal.set({ ...current, hasAvatar: false });
        }
      }),
    );
  }

  /**
   * Descarga la foto y la publica como object URL. Un 404 (usuario sin foto)
   * no es un error: simplemente deja el avatar vacío y se pintan las iniciales.
   */
  loadAvatar(): void {
    // La URL del avatar es la misma para todos los usuarios, así que el
    // navegador puede servir de su caché la foto del usuario anterior al
    // cambiar de sesión (y también la foto vieja tras subir una nueva). El
    // parámetro hace la URL distinta en cada carga y obliga a pedirla de verdad.
    const params = new HttpParams().set('t', (this.avatarCacheBuster++).toString());

    this.http
      .get(`${this.apiUrl}/users/me/avatar`, { responseType: 'blob', params })
      .pipe(catchError(() => of(null)))
      .subscribe((blob) => {
        this.setAvatarUrl(blob ? URL.createObjectURL(blob) : null);
      });
  }

  /** Limpia perfil y foto. Se llama al cerrar sesión. */
  reset(): void {
    this.profileSignal.set(null);
    this.setAvatarUrl(null);
  }

  private applyProfile(profile: MyProfile): void {
    this.profileSignal.set(profile);
    if (profile.hasAvatar) {
      this.loadAvatar();
    } else {
      this.setAvatarUrl(null);
    }
  }

  /** Revoca el object URL anterior para no dejar blobs colgando en memoria. */
  private setAvatarUrl(url: string | null): void {
    const previous = this.avatarUrlSignal();
    if (previous) {
      URL.revokeObjectURL(previous);
    }
    this.avatarUrlSignal.set(url);
  }
}
